import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Banner from "../Components/Banner";
import { deities, deitiesImages, deitiesPageData } from "../Constants/data.jsx";
import { Helmet } from "react-helmet-async";

const Deities = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredDeity, setHoveredDeity] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const combinedDeities = deities.map((deity, index) => ({
    ...deity,
    image: deitiesImages[index % deitiesImages.length],
    venue:
      deitiesPageData.details?.venue || "Sydney Sri Ayyappa Swami Centre, NSW",
    time: deitiesPageData.details?.time || "All Day",
  }));

  // Handle window resize to update isMobile state
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
    };

    // Check initially
    checkMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Carousel navigation for mobile
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % combinedDeities.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + combinedDeities.length) % combinedDeities.length
    );
  };

  // Hover handlers
  const handleMouseEnter = (index) => {
    setHoveredDeity(index);
  };

  const handleMouseLeave = () => {
    setHoveredDeity(null);
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title> Deities - Sydney Sri Ayyappa Swami Centre</title>
        <meta
          name="description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta
          name="keywords"
          content="Sydney Sri Ayyappa Swami Centre, Ayyappa Temple Sydney, Lord Ayyappa Temple Australia, Hindu Temple in Sydney, Ayyappa Pooja Sydney, Swami Ayyappa Devotees Sydney, Sabarimala Ayyappa Sydney, Hindu Religious Centre Sydney, Ayyappa Bhajans Sydney, Indian Hindu Temple Sydney"
        />

        {/* Open Graph */}
        <meta property="og:title" content="Sydney Sri Ayyappa Swami Centre" />
        <meta
          property="og:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sydneysriayyappa.com/" />

        {/* Social Links */}
        <meta
          property="og:see_also"
          content="https://www.youtube.com/@SydneySriAyyappaSwamiCentre"
        />
        <meta
          property="og:see_also"
          content="https://www.instagram.com/sydneysriayyappa/"
        />
        <meta
          property="og:see_also"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Facebook  */}

        <meta
          property="fb:admins"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Instagram */}
        <meta
          name="instagram:title"
          content="Sydney Sri Ayyappa Swami Centre"
        />
        <meta
          name="instagram:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />

        <meta name="instagram:site" content="@sydneysriayyappa" />
      </Helmet>
      <div className="container-fluid p-0">
        {/* Banner Section */}
        <Banner bannerData={deitiesPageData} />

        {/* Deities Grid Section */}
        <section>
          <div className="container mb-5">
            <h4 className="fw-bold mt-5  text-center text-md-center fs-1 heading">
              {deitiesPageData.mainHeading}
            </h4>

            {/* Desktop Grid Layout */}
            {!isMobile && (
              <div className="container py-4">
                <div className="row g-4">
                  {combinedDeities.map((deity, index) => (
                    <motion.div
                      key={`${deity.title}-${index}`}
                      className="col-12 col-sm-6 col-lg-4 p-4"
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.5,
                        ease: "easeOut",
                        delay: index * 0.1,
                      }}
                    >
                      <div
                        className="deity-card position-relative overflow-hidden rounded shadow-lg"
                        onMouseEnter={() => handleMouseEnter(index)}
                        onMouseLeave={handleMouseLeave}
                        style={{ cursor: "pointer", height: "400px" }}
                      >
                        {/* Image */}
                        <motion.img
                          src={deity.image}
                          alt={deity.title}
                          className="img-fluid w-100 h-100 object-fit-cover"
                          animate={{
                            scale: hoveredDeity === index ? 1.1 : 1,
                          }}
                          transition={{ duration: 0.3 }}
                        />

                        {/* Overlay Content */}
                        <motion.div
                          className={`deity-hover-overlay position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center p-3 text-white ${
                            hoveredDeity === index ? "show-overlay" : ""
                          }`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: hoveredDeity === index ? 1 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <motion.h5
                            className="text-center fw-bold mb-3"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{
                              y: hoveredDeity === index ? 0 : 20,
                              opacity: hoveredDeity === index ? 1 : 0,
                            }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                          >
                            {deity.title}
                          </motion.h5>

                          <motion.p
                            className="text-center small lh-sm mb-2"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{
                              y: hoveredDeity === index ? 0 : 20,
                              opacity: hoveredDeity === index ? 1 : 0,
                            }}
                            transition={{ duration: 0.3, delay: 0.2 }}
                          >
                            {deity.description}
                          </motion.p>

                          {deity.description2 && (
                            <motion.p
                              className="text-center small lh-sm"
                              initial={{ y: 20, opacity: 0 }}
                              animate={{
                                y: hoveredDeity === index ? 0 : 20,
                                opacity: hoveredDeity === index ? 1 : 0,
                              }}
                              transition={{ duration: 0.3, delay: 0.3 }}
                            >
                              {/* {deity.description2} */}
                            </motion.p>
                          )}

                          {/* Additional Info */}
                          {/* <motion.div
                            className="mt-3 text-center"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{
                              y: hoveredDeity === index ? 0 : 20,
                              opacity: hoveredDeity === index ? 1 : 0,
                            }}
                            transition={{ duration: 0.3, delay: 0.4 }}
                          >
                            <small className="d-block">
                              <strong>Venue:</strong> {deity.venue}
                            </small>
                            <small className="d-block">
                              <strong>Time:</strong> {deity.time}
                            </small>
                          </motion.div> */}
                        </motion.div>

                        {/* Title always visible at bottom with solid background */}
                        <div
                          className={`deity-overlay position-absolute bottom-0 start-0 w-100 p-3 text-white ${
                            hoveredDeity === index ? "hide-overlay" : ""
                          }`}
                        >
                          <h6 className="text-center fw-bold mb-0">
                            {deity.title}
                          </h6>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Mobile Carousel Layout */}
            {isMobile && (
              <div className="container py-4">
                <div className="row justify-content-center">
                  <div className="col-12 col-sm-10 col-md-8">
                    <div className="position-relative">
                      {/* Carousel Item */}
                      <motion.div
                        key={currentSlide}
                        className="deity-card position-relative overflow-hidden rounded shadow-lg mx-auto "
                        onMouseEnter={() => handleMouseEnter(currentSlide)}
                        onMouseLeave={handleMouseLeave}
                        style={{
                          cursor: "pointer",
                          height: "400px",
                          width: "100%",
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Image */}
                        <motion.img
                          src={combinedDeities[currentSlide].image}
                          alt={combinedDeities[currentSlide].title}
                          className="img-fluid w-100 h-100 object-fit-cover"
                          animate={{
                            scale: hoveredDeity === currentSlide ? 1.1 : 1,
                          }}
                          transition={{ duration: 0.3 }}
                        />

                        {/* Overlay Content - Same hover effect as desktop */}
                        <motion.div
                          className="position-absolute deity-hover-overlay top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center p-3 text-white"
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: hoveredDeity === currentSlide ? 1 : 0,
                          }}
                          transition={{ duration: 0.3 }}
                        >
                          <motion.h5
                            className="text-center fw-bold mb-3"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{
                              y: hoveredDeity === currentSlide ? 0 : 20,
                              opacity: hoveredDeity === currentSlide ? 1 : 0,
                            }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                          >
                            {combinedDeities[currentSlide].title}
                          </motion.h5>

                          <motion.p
                            className="text-center small lh-sm mb-2"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{
                              y: hoveredDeity === currentSlide ? 0 : 20,
                              opacity: hoveredDeity === currentSlide ? 1 : 0,
                            }}
                            transition={{ duration: 0.3, delay: 0.2 }}
                          >
                            {combinedDeities[currentSlide].description}
                          </motion.p>

                          {combinedDeities[currentSlide].description2 && (
                            <motion.p
                              className="text-center small lh-sm"
                              initial={{ y: 20, opacity: 0 }}
                              animate={{
                                y: hoveredDeity === currentSlide ? 0 : 20,
                                opacity: hoveredDeity === currentSlide ? 1 : 0,
                              }}
                              transition={{ duration: 0.3, delay: 0.3 }}
                            >
                              {/* {combinedDeities[currentSlide].description2} */}
                            </motion.p>
                          )}

                          {/* Additional Info */}
                          {/* <motion.div
                            className="mt-3 text-center"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{
                              y: hoveredDeity === currentSlide ? 0 : 20,
                              opacity: hoveredDeity === currentSlide ? 1 : 0,
                            }}
                            transition={{ duration: 0.3, delay: 0.4 }}
                          >
                            <small className="d-block">
                              <strong>Venue:</strong>{" "}
                              {combinedDeities[currentSlide].venue}
                            </small>
                            <small className="d-block">
                              <strong>Time:</strong>{" "}
                              {combinedDeities[currentSlide].time}
                            </small>
                          </motion.div> */}
                        </motion.div>

                        {/* Title always visible at bottom with solid background */}
                        <div
                          className={`position-absolute bottom-0 start-0 w-100 p-3 text-white deity-overlay ${
                            hoveredDeity === currentSlide ? "hide-overlay" : ""
                          }`}
                        >
                          <h6 className="text-center fw-bold mb-0">
                            {combinedDeities[currentSlide].title}
                          </h6>
                        </div>
                      </motion.div>

                      {/* Navigation Buttons - Outside Card, Center Alignment */}
                      <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                        <button
                          className="bi bi-arrow-left cursor-pointer fs-4 heading custom-border d-flex align-items-center justify-content-center rounded-circle"
                          onClick={prevSlide}
                          style={{
                            width: "50px",
                            height: "50px",
                          }}
                        ></button>

                        {/* Mobile Indicator */}
                        <div className="d-flex gap-2 align-items-center">
                          <span className="heading fs-6 fw-medium">
                            {currentSlide + 1} / {combinedDeities.length}
                          </span>
                        </div>

                        <button
                          className="bi bi-arrow-right cursor-pointer fs-4 heading custom-border d-flex align-items-center justify-content-center rounded-circle"
                          onClick={nextSlide}
                          style={{
                            width: "50px",
                            height: "50px",
                          }}
                        ></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Deities;
